import { useEffect, useRef, useState } from "react";
import {
  Alert,
  Button,
  Card,
  CardBody,
  CardFooter,
  CardHeader,
  Col,
  Container,
  Form,
  Input,
  Label,
  Row,
} from "reactstrap";
import { useFormik } from "formik";
import { Link, useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import Flatpickr from "react-flatpickr";
import moment from "moment";
import BreadCrumb from "components/common/BreadCrumb";
import { BackButton } from "components/common/Buttons";
import {
  FileDropzone,
  DEFAULT_IMAGES_AND_PDF_ACCEPT,
} from "components/common/FileDropzone";
import { formatFileSize } from "utils/fileUtils";
import { getDocumentTitle } from "utils/helpers";
import { getInvoice, updateInvoice } from "../services/InvoiceService";
import {
  InvoiceSourceType,
  InvoiceSourceTypeLabelMap,
  InvoiceStatus,
  InvoiceStatusMap,
} from "../constants/invoice.constants";
import type { Invoice } from "../types/invoice.types";
import { FileTypeIcon } from "components/common/FileTypeIcon";
import StatusBadge from "components/common/StatusBadge";
import { DateOnly } from "components/common/Date";
import { formatCurrency } from "utils/currency";
import { getClientDisplayName } from "features/Clients/utils/clientDisplayName";
import {
  AnySourceType,
  SOURCE_TYPE_URL_MAP,
} from "constants/source-type.constants";
import createSchema from "./schema";

const MAX_FILE_BYTES = 10 * 1024 * 1024;
const MAX_FILES = 10;

type FileWithPreview = File & { preview?: string };

function apiErrorMessage(err: unknown, fallback: string): string {
  const msg = (err as { response?: { data?: { message?: string } } })?.response
    ?.data?.message;
  return typeof msg === "string" && msg.trim() ? msg : fallback;
}

const InvoiceForm = () => {
  document.title = getDocumentTitle("Edit Invoice");
  const navigate = useNavigate();
  const { id } = useParams();
  const [record, setRecord] = useState<Invoice | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [selectedFiles, setSelectedFiles] = useState<FileWithPreview[]>([]);
  const selectedFilesRef = useRef<FileWithPreview[]>([]);
  selectedFilesRef.current = selectedFiles;
  const [deletedAttachments, setDeletedAttachments] = useState<string[]>([]);
  useEffect(() => {
    return () => {
      selectedFilesRef.current.forEach((f) => {
        if (f.preview?.startsWith("blob:")) URL.revokeObjectURL(f.preview);
      });
    };
  }, []);

  useEffect(() => {
    if (!id) return;
    const numId = Number(id);
    if (!numId) return;
    getInvoice(numId)
      .then((res) => {
        if (res.success) setRecord(res.data);
        else setLoadError("Could not load invoice.");
      })
      .catch(() => setLoadError("Could not load invoice."));
  }, [id]);

  const validation = useFormik({
    enableReinitialize: true,
    initialValues: {
      code: record?.code ?? "",
      dueDate: record?.dueDate ?? "",
    },
    validationSchema: createSchema(record?.issueDate),
    onSubmit: async (values, { setSubmitting }) => {
      try {
        const formData = new FormData();
        const code = values.code?.trim();
        if (code) formData.append("code", code);
        if (values.dueDate) formData.append("dueDate", values.dueDate);
        selectedFiles.forEach((f) => formData.append("files", f));
        deletedAttachments.forEach((url) =>
          formData.append("removeAttachments", url),
        );

        const res = await updateInvoice(Number(id), formData);
        if (res.success) {
          toast.success(res.message ?? "Invoice updated");
          navigate(-1);
        }
      } catch (err) {
        toast.error(apiErrorMessage(err, "Could not save invoice"));
      } finally {
        setSubmitting(false);
      }
    },
  });

  const handleFileDrop = (files: File[]) => {
    setSelectedFiles((prev) => {
      const remaining = MAX_FILES - prev.length;
      if (remaining <= 0) {
        toast.warning(`Maximum ${MAX_FILES} files allowed.`);
        return prev;
      }
      const accepted = files.slice(0, remaining);
      if (accepted.length < files.length) {
        toast.warning(
          `Only ${accepted.length} file(s) added — max ${MAX_FILES} total.`,
        );
      }
      const withPreview = accepted.map((f) =>
        Object.assign(f, {
          preview: f.type.startsWith("image/")
            ? URL.createObjectURL(f)
            : undefined,
        }),
      );
      return [...prev, ...withPreview];
    });
  };

  const removeFile = (index: number) => {
    setSelectedFiles((prev) => {
      const removed = prev[index];
      if (removed?.preview?.startsWith("blob:"))
        URL.revokeObjectURL(removed.preview);
      return prev.filter((_, i) => i !== index);
    });
  };

  if (loadError) {
    return (
      <div className="page-content">
        <Container fluid>
          <Alert color="danger">{loadError}</Alert>
        </Container>
      </div>
    );
  }

  const statusMeta = record ? InvoiceStatusMap[record.status] : null;

  return (
    <div className="page-content">
      <Container fluid>
        <BreadCrumb
          title="Edit Invoice"
          pageTitle="Finance"
          items={[{ label: "Invoices", path: "/finance/invoices" }]}
        />
        <Form onSubmit={validation.handleSubmit}>
          <Row className="g-4">
            <Col lg={8}>
              <Card>
                <CardHeader>
                  <div className="d-flex align-items-center gap-3">
                    <h5 className="card-title mb-0">Invoice details</h5>
                    {statusMeta ? (
                      <span
                        className={`badge bg-${statusMeta.color}-subtle text-${statusMeta.color}`}
                      >
                        {statusMeta.label}
                      </span>
                    ) : null}
                  </div>
                </CardHeader>
                <CardBody className="vstack gap-3">
                  {/* Code */}
                  <div>
                    <Label className="form-label fw-medium">Invoice code</Label>
                    <Input
                      type="text"
                      name="code"
                      value={validation.values.code}
                      onChange={validation.handleChange}
                      onBlur={validation.handleBlur}
                      placeholder="e.g. INV-2024-001"
                      disabled={record?.status !== InvoiceStatus.ISSUED}
                    />
                  </div>

                  {/* Due date */}
                  <div>
                    <Label className="form-label fw-medium">Due date</Label>
                    <Flatpickr
                      className="form-control"
                      value={
                        validation.values.dueDate
                          ? moment(validation.values.dueDate).toDate()
                          : undefined
                      }
                      options={{ dateFormat: "d/m/Y" }}
                      onChange={([date]) => {
                        void validation.setFieldValue(
                          "dueDate",
                          date ? moment(date).format("YYYY-MM-DD") : "",
                        );
                        void validation.setFieldTouched("dueDate", true);
                      }}
                      placeholder="dd/mm/yyyy"
                    />
                    {validation.touched.dueDate && validation.errors.dueDate ? (
                      <div className="text-danger small mt-1">
                        {validation.errors.dueDate}
                      </div>
                    ) : null}
                  </div>

                  {/* Existing attachments */}
                  {(record?.attachments?.length ?? 0) > 0 ? (
                    <div>
                      <Label className="form-label fw-medium">
                        Existing files
                      </Label>
                      <div className="vstack gap-2">
                        {record?.attachments
                          ?.filter((url) => !deletedAttachments.includes(url))
                          .map((url, idx) => {
                            const filename = decodeURIComponent(
                              url.split("/").pop()?.split("?")[0] ??
                                `File ${idx + 1}`,
                            );
                            return (
                              <div
                                key={url}
                                className="d-flex align-items-center gap-2 border rounded p-2 bg-light bg-opacity-50"
                              >
                                <FileTypeIcon
                                  filename={url}
                                  imageSrc={url}
                                  className="rounded flex-shrink-0"
                                  style={{ width: 40, height: 40 }}
                                />
                                <div className="flex-grow-1 min-w-0">
                                  <div className="text-truncate small fw-medium">
                                    {filename}
                                  </div>
                                </div>
                                <a
                                  href={url}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="btn btn-soft-secondary btn-sm px-2 py-1 flex-shrink-0"
                                >
                                  <i className="ri-eye-line" />
                                </a>
                                <Button
                                  type="button"
                                  color="danger"
                                  outline
                                  size="sm"
                                  className="px-2 py-1 flex-shrink-0"
                                  title="Remove attachment"
                                  onClick={() =>
                                    setDeletedAttachments((prev) => [
                                      ...prev,
                                      url,
                                    ])
                                  }
                                >
                                  <i className="ri-delete-bin-line" />
                                </Button>
                              </div>
                            );
                          })}
                        {deletedAttachments.length > 0 ? (
                          <p className="text-muted small mb-0">
                            <i className="ri-information-line me-1" />
                            {deletedAttachments.length} file
                            {deletedAttachments.length > 1 ? "s" : ""} will be
                            removed on save.{" "}
                            <button
                              type="button"
                              className="btn btn-link btn-sm p-0 align-baseline"
                              onClick={() => setDeletedAttachments([])}
                            >
                              Undo
                            </button>
                          </p>
                        ) : null}
                      </div>
                    </div>
                  ) : null}

                  {/* File upload */}
                  <div>
                    <Label className="form-label fw-medium">
                      Upload additional files
                      {selectedFiles.length > 0 ? (
                        <span className="text-muted fw-normal ms-1">
                          ({selectedFiles.length}/{MAX_FILES})
                        </span>
                      ) : null}
                    </Label>
                    <FileDropzone
                      accept={DEFAULT_IMAGES_AND_PDF_ACCEPT}
                      maxSize={MAX_FILE_BYTES}
                      multiple
                      disabled={selectedFiles.length >= MAX_FILES}
                      onDropAccepted={handleFileDrop}
                      compact
                      description={`PNG, JPG, PDF · Max ${formatFileSize(MAX_FILE_BYTES)} per file · Up to ${MAX_FILES} files`}
                      notifyOnReject
                    />

                    {selectedFiles.length > 0 ? (
                      <div className="mt-3 vstack gap-2">
                        {selectedFiles.map((file, idx) => (
                          <div
                            key={idx}
                            className="d-flex align-items-center gap-2 border rounded p-2"
                          >
                            <FileTypeIcon
                              filename={file.name}
                              imageSrc={file.preview}
                              className="rounded flex-shrink-0"
                              style={{ width: 40, height: 40 }}
                            />
                            <div className="flex-grow-1 min-w-0">
                              <div className="text-truncate small fw-medium">
                                {file.name}
                              </div>
                              <div
                                className="text-muted"
                                style={{ fontSize: "0.75rem" }}
                              >
                                {formatFileSize(file.size)}
                              </div>
                            </div>
                            <Button
                              type="button"
                              close
                              onClick={() => removeFile(idx)}
                            />
                          </div>
                        ))}
                      </div>
                    ) : null}
                  </div>
                </CardBody>
                <CardFooter>
                  <div className="d-flex justify-content-end gap-2">
                    <BackButton />
                    <Button
                      type="submit"
                      color="primary"
                      disabled={validation.isSubmitting}
                    >
                      {validation.isSubmitting ? (
                        <>
                          <span
                            className="spinner-border spinner-border-sm me-1"
                            role="status"
                            aria-hidden
                          />
                          Saving…
                        </>
                      ) : (
                        "Save changes"
                      )}
                    </Button>
                  </div>
                </CardFooter>
              </Card>
            </Col>

            <Col lg={4}>
              {record
                ? (() => {
                    const sourceBase =
                      record.sourceType != null
                        ? SOURCE_TYPE_URL_MAP[
                            record.sourceType as AnySourceType
                          ]
                        : undefined;
                    const clientName = record.client
                      ? getClientDisplayName(record.client)
                      : null;
                    const sourceLabel =
                      record.sourceType != null
                        ? (InvoiceSourceTypeLabelMap[
                            record.sourceType as InvoiceSourceType
                          ] ?? record.sourceType)
                        : null;
                    return (
                      <Card>
                        <CardHeader>
                          <h5 className="card-title mb-0">Invoice summary</h5>
                        </CardHeader>
                        <CardBody className="vstack gap-3">
                          <div>
                            <p
                              className="text-muted small mb-1 text-uppercase fw-semibold"
                              style={{ fontSize: "0.7rem" }}
                            >
                              Status
                            </p>
                            <StatusBadge
                              value={record.status}
                              map={InvoiceStatusMap}
                            />
                          </div>
                          {record.client ? (
                            <div>
                              <p
                                className="text-muted small mb-1 text-uppercase fw-semibold"
                                style={{ fontSize: "0.7rem" }}
                              >
                                Client
                              </p>
                              <Link
                                to={`/clients/${record.clientId}`}
                                className="fw-medium small link"
                              >
                                {clientName && clientName !== "—"
                                  ? clientName
                                  : record.client.email ||
                                    `Client #${record.clientId}`}
                              </Link>
                            </div>
                          ) : null}
                          {sourceLabel ? (
                            <div>
                              <p
                                className="text-muted small mb-1 text-uppercase fw-semibold"
                                style={{ fontSize: "0.7rem" }}
                              >
                                Source
                              </p>
                              <span className="small">{sourceLabel}</span>
                              {sourceBase &&
                              record.sourceId &&
                              record.source?.code ? (
                                <Link
                                  to={`${sourceBase}/${record.sourceId}`}
                                  className="d-block fw-medium small link"
                                >
                                  {record.source.code}
                                </Link>
                              ) : null}
                            </div>
                          ) : null}
                          {record.issueDate ? (
                            <div>
                              <p
                                className="text-muted small mb-1 text-uppercase fw-semibold"
                                style={{ fontSize: "0.7rem" }}
                              >
                                Issue date
                              </p>
                              <span className="small">
                                <DateOnly date={record.issueDate} />
                              </span>
                            </div>
                          ) : null}
                          {record.dueDate ? (
                            <div>
                              <p
                                className="text-muted small mb-1 text-uppercase fw-semibold"
                                style={{ fontSize: "0.7rem" }}
                              >
                                Due date
                              </p>
                              <span className="small">
                                <DateOnly date={record.dueDate} />
                              </span>
                            </div>
                          ) : null}
                          <div className="border-top pt-3 vstack gap-2">
                            {record.totalAmount != null ? (
                              <div className="d-flex justify-content-between align-items-baseline">
                                <span className="text-muted small">Total</span>
                                <span className="fw-semibold tabular-nums small">
                                  {formatCurrency(record.totalAmount)}
                                </span>
                              </div>
                            ) : null}
                            <div className="d-flex justify-content-between align-items-baseline">
                              <span className="text-muted small">
                                Outstanding
                              </span>
                              <span className="fw-semibold tabular-nums">
                                {formatCurrency(record.outstandingAmount)}
                              </span>
                            </div>
                          </div>
                        </CardBody>
                      </Card>
                    );
                  })()
                : null}
            </Col>
          </Row>
        </Form>
      </Container>
    </div>
  );
};

export default InvoiceForm;
